import React, { forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Container,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from '@mui/material';
import { useJsonContext } from '../pages/aptitudeTest';
import LightbulbIcon from '@mui/icons-material/Lightbulb';

const exampleData = [
  { questionId: 'W', questionImageName: '/test_images/exsample_image/question_image/exsample_q_5.jpg', correctImageName: '/test_images/exsample_image/answer_image/exsample_a_5.jpg', correct: 1, hintText: "例題Wでは、斜線を引いた四角が時計の針が動く方向へ順次動いています。<br/>すなわち右上、右下、左下、左上という順序です。<br/>従って、次は右上となり、Bが正解となります。"},
  { questionId: 'X', questionImageName: '/test_images/exsample_image/question_image/exsample_q_6.jpg', correctImageName: '/test_images/exsample_image/answer_image/exsample_a_6.jpg', correct: 4, hintText: "左側の一連の図では線が1本ずつ増え、かつ次第に長くなっていきます。<br/>右側の5つの図の中から次に来るべき図を選んで、チェックボックスにチェックを入れてください。<br/>正解はEです。線は5本で左側の4番目の図よりも1本多く、5本目の線がやや長くなっています。"}
];

const ExampleTestPart2 = forwardRef((props, ref) => {

  const { jsonData } = useJsonContext();

  useImperativeHandle(ref, () => ({
    handleButtonClick,
  }));

  const handleButtonClick = () => {
    console.log(`開始clicked`);
    console.log("解答データ (JSON):", (JSON.stringify(jsonData)));
  };

  return (
    <Container component="main">
      <Box>
        {/* *** 2部例題 *** */}
        <Box sx={{ marginY: 2 }}>
          <Typography
            variant="h5"
            sx={{
              margin: '1rem 0',
              paddingLeft: '10px',
              borderLeft: 'solid 3px blue',
              fontWeight: 'bold',
              lineHeight: '3rem',
              height: '2.8rem',
              backgroundColor: 'divider',
              fontSize: { xs: '1.2rem', sm: '1.5rem' },
            }}
          >
            第2部 例題と説明
          </Typography>

          <Box sx={{ paddingLeft: { xs: '10px', sm: '30px' } }}>
            <Typography sx={{ lineHeight: '4rem', height: '4rem', fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              第2部には次のような問題が出題されます。
            </Typography>
            <Box sx={{ backgroundColor: '#e3f2fd', padding: 2 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                各列の左側にある4つの図は、ある一定の規則で並んでいます。
                <br />
                次に来るべき図を右側の5つの図の中から選んでください。
              </Typography>
            </Box>
            <Typography sx={{ lineHeight: '4rem', height: '4rem', fontWeight: 'bold', fontSize: { xs: '0.9rem', sm: '1rem' } }}>
              次の例題をご覧ください。
            </Typography>
          </Box>
        </Box>
        <Box>
          {/* Example questions */}
          {exampleData.map((example, index) => (
            <Box
              key={index}
              sx={{
                margin: { xs: '2rem 0', sm: '' },
                padding: { xs: '0 10px', sm: '50px 50px 40px 50px' },
                borderBottom: "solid 1px gray",
              }}
            >
              <Typography sx={{ height: '3.5rem' }}>
                <Box component="span"
                  sx={{
                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                    padding: '0.3rem 1.5rem',
                    borderRadius: "9%",
                    color: "white",
                    backgroundColor: "#009ce5",
                  }}
                >
                  例題 {example.questionId}
                </Box>
              </Typography>
              <Box
                sx={{
                  padding: { xs: '0.5rem 0 0', sm: '0.5rem 2rem 0', md: '3rem 3rem 1rem' },
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '0.2fr 1.6fr',
                    md: 'repeat(3, 1fr)',
                  },
                  gridTemplateRows: {
                    xs: 'auto auto auto',
                    md: 'auto auto',
                  },
                  columnGap: 2,
                  rowGap: 1,
                  alignItems: { xs: 'start', md: 'center' },
                }}
              >

                {/* 問題画像 */}
                <Box
                  component="img"
                  src={'https://dev-aptitude-test-api-image-bucket.s3.ap-northeast-1.amazonaws.com' + example.questionImageName}
                  alt="例題の質問"
                  sx={{
                    width: '100%',
                    maxWidth: { xs: '70%', sm: '60%', md: '100%' },
                    objectFit: 'contain',
                    justifySelf: { xs: 'start', md: 'center' },
                    maxHeight: { xs: '4rem', sm: '4.6rem', md: '5rem' },

                    // 配置切替
                    gridColumn: { xs: '2 / 3', md: '1 / 2' },
                    gridRow: { xs: '1 / 2', md: '1 / 2' },
                  }}
                />

                {/* 解答文字 */}
                <Typography
                  sx={{
                    fontSize: "1.1rem",
                    fontWeight: "bold",
                    textAlign: { xs: 'left', md: 'right' },
                    alignSelf: 'center',

                    gridColumn: { xs: '1 / 2', md: '2 / 3' },
                    gridRow: { xs: '2 / 3', md: '1 / 2' },
                  }}
                >
                  解答
                </Typography>

                {/* 解答画像 */}
                <Box
                  component="img"
                  src={'https://dev-aptitude-test-api-image-bucket.s3.ap-northeast-1.amazonaws.com' + example.correctImageName}
                  alt={`例題${example.questionId}解答`}
                  sx={{
                    width: '100%',
                    maxWidth:  { xs: '70%', sm: '60%', md: '100%' },
                    objectFit: 'contain',
                    justifySelf: { xs: 'start', md: 'center' },

                    gridColumn: { xs: '2 / 3', md: '3 / 4' },
                    gridRow: { xs: '2 / 3', md: '1 / 2' },
                  }}
                />

                {/* ラジオボタン */}
                <FormControl
                  component="fieldset"
                  sx={{
                    width: '100%',
                    maxWidth:  { xs: '70%', sm: '60%', md: '100%' },
                    gridColumn: { xs: '2 / 3', md: '3 / 4' },
                    gridRow: { xs: '3 / 4', md: '2 / 3' },
                  }}
                >
                  <RadioGroup
                    row
                    name={`choice-${example.questionId}`}
                    defaultValue="checked"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(5, 1fr)',
                      justifyItems: 'center',
                    }}
                  >
                    {Array(5)
                      .fill(0)
                      .map((_, i) => (
                        <FormControlLabel
                          key={i}
                          value={`choice-${i}`}
                          control={
                            <Radio
                              disabled={i !== example.correct}
                              checked={i === example.correct}
                            />
                          }
                          label=""
                          sx={{ margin: 0 }}
                        />
                      ))}
                  </RadioGroup>
                </FormControl>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  border: 'solid 2px #009ce5',
                  padding: '1rem',
                  marginTop: '2rem',
                  marginBottom: { xs: '2rem' },
                }}
              >
                <Typography
                  sx={{
                    color: '#009ce5',
                    fontSize: '1.2rem',
                    fontWeight: 'bold',
                    display: 'flex',
                    alignItems: 'center',
                    marginRight: '1rem',
                    minWidth: '9%',
                    marginBottom: { xs: '0.5rem', sm: 0 }
                  }}
                >
                  <LightbulbIcon sx={{ marginRight: '0.3rem' }} />
                  解説
                </Typography>
                <Typography sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
                  <p dangerouslySetInnerHTML={{ __html: example.hintText }} style={{ margin: 0 }} />
                </Typography>
              </Box>
            </Box>
          ))}

          <Box sx={{ margin: { xs: '3rem 0', sm: '6rem 0 5rem 0' } }}>
            <Typography variant="h5" sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' }, textAlign: 'center', fontWeight: 'bold' }}>
              例題がよく理解できたら、開始ボタンを押して始めてください。
              <br />
              第2部の制限時間は<span style={{ color: '#1976d2' }}>15分</span>です。
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
    
  );
});

export default ExampleTestPart2;